import { useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { CarerNamesInput } from '../CarerNamesInput';
import MonthYearPicker from '../MonthYearPicker';
import { RespiteCarerPicker } from '../RespiteCarerPicker';
import { PLACEMENT_TYPES, CARER_LED_TYPES } from '../../lib/orgTypes';

const BLANK = { type: 'foster_placement', name: '', address: '', carer_names: [], respite: [], start_date: '', end_date: '' };

/** "Add a home" — somewhere the person lived while this organisation was responsible for them. */
export const AddHomeDialog = ({ open, onOpenChange, orgName, respitePool, onPoolChange, onAdd, saving }) => {
  const [home, setHome] = useState(BLANK);
  const carerLed = CARER_LED_TYPES.has(home.type);
  const submit = async () => {
    if (await onAdd(home)) {
      setHome(BLANK);
      onOpenChange(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="add-placement-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-[22px] font-semibold tracking-[-0.02em]">Add a home</DialogTitle>
          <DialogDescription className="text-ink-55">Somewhere you lived while {orgName} was responsible for you.</DialogDescription>
        </DialogHeader>
        <div className="space-y-6 py-2">
          <div className="grid sm:grid-cols-2 gap-5">
            <div className="space-y-2">
              <Label className="text-[15px] font-semibold">What kind of home was it?</Label>
              <Select value={home.type} onValueChange={(v) => setHome({ ...home, type: v })}>
                <SelectTrigger data-testid="add-placement-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PLACEMENT_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-[15px] font-semibold" htmlFor="add-home-name">What do you call it?</Label>
              <Input id="add-home-name" value={home.name} onChange={(e) => setHome({ ...home, name: e.target.value })}
                placeholder={carerLed ? 'For example, The Smiths' : 'Name of the home or unit'} data-testid="add-placement-name" />
            </div>
          </div>
          <div className="space-y-2">
            <Label className="text-[15px] font-semibold">{carerLed ? 'Who looked after you there?' : 'Who worked with you there?'}</Label>
            <p className="text-sm text-ink-55">{carerLed ? 'Foster carers, family carers or hosts.' : 'Key workers, residential staff or carers.'}</p>
            <CarerNamesInput value={home.carer_names} onChange={(arr) => setHome({ ...home, carer_names: arr })}
              placeholder="Type a name and press Enter" testId="add-placement-people" />
          </div>
          <div className="space-y-2">
            <Label className="text-[15px] font-semibold" htmlFor="add-home-address">Where was it?</Label>
            <p className="text-sm text-ink-55">An address, a street or just the town. Whatever you remember.</p>
            <Input id="add-home-address" value={home.address} onChange={(e) => setHome({ ...home, address: e.target.value })} data-testid="add-placement-address" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div className="space-y-2">
              <Label className="text-[15px] font-semibold">When did you move in?</Label>
              <MonthYearPicker value={home.start_date} onChange={(v) => setHome({ ...home, start_date: v })} ariaLabel="Moved in" />
            </div>
            <div className="space-y-2">
              <Label className="text-[15px] font-semibold">When did you leave?</Label>
              <MonthYearPicker value={home.end_date} onChange={(v) => setHome({ ...home, end_date: v })} ariaLabel="Left" />
            </div>
          </div>
          <div className="space-y-2">
            <Label className="text-[15px] font-semibold">Did anyone else look after you for short breaks?</Label>
            <RespiteCarerPicker
              attached={home.respite || []}
              pool={respitePool}
              onChange={({ attached, pool }) => {
                setHome({ ...home, respite: attached });
                if (pool.length !== respitePool.length) onPoolChange(pool);
              }}
              testId="add-placement-respite"
            />
          </div>
        </div>
        <DialogFooter className="mt-2">
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={!home.type || !home.name || saving} data-testid="add-placement-submit">
            {saving ? 'Adding' : 'Add home'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AddHomeDialog;
